import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import loOmit from 'lodash/omit';

import { getEmptyErrorState } from './AppUtil';
import CyberdyneValidator from './cyberdyneValidator';
import ErrorMessageGenerator from './ErrorMessageGenerator';

const errMsgGenerator = new ErrorMessageGenerator();
const cyberdyneValidator = new CyberdyneValidator();

export default class CustomFieldUtil {
  public isFieldValueEmpty = (value: unknown, type: CustomFieldType) => {
    if (value === 0) {
      return false;
    }

    return !value || (type !== CustomFieldType.NUMBER && type !== CustomFieldType.DATE && !String(value).trim().length);
  };

  public getRequestKeyValues(fieldValues: IFieldValues | IFieldState[] | ICRMCustomField[]) {
    const requestKeyValues = {};
    for (const fieldId in fieldValues) {
      if (fieldValues[fieldId]) {
        requestKeyValues[fieldValues[fieldId]._id] =
          fieldValues[fieldId].type === CustomFieldType.NUMBER && !!fieldValues[fieldId].value.length
            ? +fieldValues[fieldId].value
            : fieldValues[fieldId].value;
      }
    }

    return requestKeyValues;
  }

  public setRequestKeyValues(fields: IFieldState[], fieldValues: any) {
    const requestKeyValues = {};
    for (const field of fields) {
      if (fieldValues[field.id]) {
        field.value = fieldValues[field.id];
      }
    }

    return requestKeyValues;
  }

  public validateFieldValues = (fieldValuesState: IFieldState[]): { fieldValues: IFieldState[]; isValid: boolean } => {
    let isValid = true;

    const validateFieldsRecursively = (fields: IFieldState[]) => {
      for (const field of fields) {
        if (!field.isDeleted && field.isRequired && this.isFieldValueEmpty(field.value, field.type) && field.isActive) {
          if (
            !field.hasOwnProperty('displaySecondaryCustomField') ||
            (field.hasOwnProperty('displaySecondaryCustomField') && field.displaySecondaryCustomField)
          ) {
            field.error = {
              errorMessage: errMsgGenerator.getMandatoryFieldMessage(field.label),
              errorState: IValidationState.ERROR,
            };
            isValid = false;
          }
        } else if (cyberdyneValidator.isStringGreaterThanLength(String(field.value), 255)) {
          field.error = {
            errorMessage: errMsgGenerator.getCharacterLimitMessage(field.label, 255),
            errorState: IValidationState.ERROR,
          };
          isValid = false;
        } else {
          field.error = {
            errorMessage: '',
            errorState: null,
          };
        }

        // Recursively validate nested fields
        if (field.fields && field.fields.length > 0) {
          validateFieldsRecursively(field.fields);
        }
      }
    };

    // Clone the fieldValuesState array deeply to avoid modifying the original array
    const clonedFieldValuesState = cloneDeep(fieldValuesState);

    // Start validation recursively
    validateFieldsRecursively(clonedFieldValuesState);

    return { fieldValues: clonedFieldValuesState, isValid };
  };

  public validateCategoryFieldValues = (
    categoryfieldValues: IFieldValues
  ): { fieldValues: IFieldValues; isValid: boolean } => {
    let isValid = true;
    const updateFieldErrorState = produce(categoryfieldValues, (draft) => {
      for (const fieldId in categoryfieldValues) {
        if (categoryfieldValues[fieldId]) {
          const fieldValue = categoryfieldValues[fieldId];
          const value = fieldValue.value;

          if (fieldValue && fieldValue.isRequired && this.isFieldValueEmpty(value, fieldValue.type)) {
            draft[fieldId].error.errorState = IValidationState.ERROR;
            draft[fieldId].error.errorMessage = errMsgGenerator.getMandatoryFieldMessage(fieldValue.label);
            isValid = false;
          } else if (cyberdyneValidator.isStringGreaterThanLength(String(fieldValue.value), 255)) {
            draft[fieldId].error.errorState = IValidationState.ERROR;
            draft[fieldId].error.errorMessage = errMsgGenerator.getCharacterLimitMessage(fieldValue.label, 255);
            isValid = false;
          } else {
            draft[fieldId].error.errorState = null;
            draft[fieldId].error.errorMessage = null;
          }
        }
      }
    });

    return { fieldValues: updateFieldErrorState, isValid };
  };

  getDefaultFieldValuesState = (
    fields: any[],
    fieldValues?: Record<string, unknown>,
    activeOnly: boolean = false
  ): IFieldState[] => {
    const defaultFieldValues = fields
      ? fields.reduce((acc, curr) => {
          const fieldState: IFieldState = {
            _id: curr._id || curr.id,
            id: curr._id || curr.id,
            isRequired: curr.isRequired,
            isActive: curr.isActive,
            label: curr.label,
            type: curr.type,
            options: curr.options,
            displayOptions: curr.displayOptions,
            value: fieldValues ? fieldValues[curr.id] : '',
            error: getEmptyErrorState(),
            fields: curr.fields,
            displaySecondaryCustomField: curr.displaySecondaryCustomField,
          };

          if (activeOnly && curr.isActive) {
            acc.push(fieldState);
          } else if (!activeOnly && fieldValues.hasOwnProperty(`${curr._id}`)) {
            acc.push(fieldState);
          }

          return acc;
          // tslint:disable-next-line: align
        }, [])
      : [];

    return defaultFieldValues;
  };

  public static mapFieldsForRendering = (fieldValues: IFieldState[]): IFieldState[][] => {
    const fieldForRendering = [];
    fieldValues?.forEach((f) => {
      const flattenedFields = [];
      this.flattenFields(f, flattenedFields);
      fieldForRendering.push(flattenedFields);
    });

    return fieldForRendering;
  };

  private static flattenFields = (field, flattenedFields) => {
    const fr = loOmit(field, 'fields');
    const isTrue = field.isActive || (field.hasOwnProperty('value') && field.value !== '');
    const isDeletedInclude =
      (field.isDeleted && field.hasOwnProperty('value') && field.value !== '') || !field.isDeleted;
    if (isTrue && isDeletedInclude) {
      flattenedFields.push(fr);
    }
    if (field.fields?.length) {
      field.fields.forEach((f) => this.flattenFields(f, flattenedFields));
    }
  };

  public static findActiveFields = (fields: IFieldState[]): IFieldState[] => {
    const activeFields = [];
    fields.forEach((field) => {
      if (field.isActive) {
        activeFields.push(field);
      }
    });

    return activeFields;
  };
}
